import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import DashboardLayout from '../components/layout/DashboardLayout';
import { DollarSign, Clock, Pause, TrendingDown, AlertCircle, CheckCircle } from 'lucide-react';
import { calculateCoolingOff } from '../utils/coolingOff';
import { devLog } from '../utils/devLogger';

const CancelSubscription = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cancelReason, setCancelReason] = useState('');
  const [otherReason, setOtherReason] = useState('');
  const [cancelled, setCancelled] = useState(false);

  const subscription = user?.subscriptionDetails || {};
  const coolingOff = calculateCoolingOff(subscription.subscribedDate);

  const handleCancel = () => {
    // Send cancellation email (TODO: wire to Postmark)
    devLog(`📧 [mock] Cancellation email queued for ${user?.email} — refund $${subscription.totalPaid} via ${subscription.paymentMethod}`);

    setCancelled(true);
  };

  if (!subscription.plan) {
    return (
      <DashboardLayout>
        <div className="p-8 max-w-2xl mx-auto">
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 text-center">
            <AlertCircle size={48} className="mx-auto text-yellow-600 mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">No Active Subscription</h2>
            <p className="text-gray-600">You don&apos;t have an active subscription to cancel.</p>
            <button
              onClick={() => navigate('/settings')}
              className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Return to Settings
            </button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (cancelled) {
    return (
      <DashboardLayout>
        <div className="p-8 max-w-2xl mx-auto">
          <div className="bg-white rounded-xl shadow-xl p-8">
            <div className="text-center mb-6">
              <CheckCircle size={64} className="mx-auto text-green-600 mb-4" />
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Cancellation Confirmed</h1>
              <p className="text-gray-600">Your subscription has been cancelled.</p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-6">
              <h3 className="font-bold text-blue-900 mb-3">Refund Details:</h3>
              <div className="space-y-2 text-sm text-blue-800">
                <div className="flex justify-between">
                  <span>Amount:</span>
                  <span className="font-semibold">${subscription.totalPaid}</span>
                </div>
                <div className="flex justify-between">
                  <span>Method:</span>
                  <span className="font-semibold">{subscription.paymentMethod}</span>
                </div>
                <div className="flex justify-between">
                  <span>Expected:</span>
                  <span className="font-semibold">Within 5-7 business days</span>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 mb-6">
              <h3 className="font-bold text-gray-900 mb-3">What Happens Next:</h3>
              <ol className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="font-bold">1.</span>
                  <span>You can still use HiBFF until your refund is processed</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-bold">2.</span>
                  <span>Refund will appear on your statement as &quot;HiBFF REFUND&quot;</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-bold">3.</span>
                  <span>Your data will be retained for 30 days in case you change your mind</span>
                </li>
              </ol>
            </div>

            <div className="text-center mb-6">
              <p className="text-sm text-gray-600">
                📧 Confirmation email sent to: <strong>{user?.email}</strong>
              </p>
            </div>

            <div className="bg-purple-50 border border-purple-200 rounded-xl p-6 text-center mb-6">
              <p className="text-sm font-semibold text-purple-900 mb-3">Changed your mind?</p>
              <button
                onClick={() => navigate('/settings')}
                className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              >
                Reactivate Subscription
              </button>
              <p className="text-xs text-purple-700 mt-2">Available for 30 days</p>
            </div>

            <button
              onClick={() => navigate('/')}
              className="w-full py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50"
            >
              Return to Dashboard
            </button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-8 max-w-3xl mx-auto">
        <div className="bg-white rounded-xl shadow-xl p-8">
          {/* Header */}
          <div className="text-center mb-6">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">😢 We&apos;re Sorry to See You Go</h1>
          </div>

          {/* Subscription Details */}
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 mb-6">
            <h3 className="font-bold text-gray-900 mb-3">Your Subscription:</h3>
            <div className="space-y-2 text-sm text-gray-700">
              <div className="flex justify-between">
                <span>Plan:</span>
                <span className="font-semibold">{subscription.plan} (${subscription.price}/month)</span>
              </div>
              <div className="flex justify-between">
                <span>Subscribed:</span>
                <span className="font-semibold">{subscription.subscribedDate}</span>
              </div>
              <div className="flex justify-between">
                <span>Total Paid:</span>
                <span className="font-semibold">${subscription.totalPaid}</span>
              </div>
            </div>
          </div>

          {/* Refund Eligibility */}
          {coolingOff.refundEligible ? (
            <div className="bg-green-50 border-2 border-green-300 rounded-xl p-6 mb-6">
              <div className="flex items-start gap-4">
                <DollarSign size={48} className="text-green-600 flex-shrink-0" />
                <div>
                  <h3 className="text-xl font-bold text-green-900 mb-2">💰 You&apos;re Eligible for Full Refund</h3>
                  <div className="space-y-1 text-sm text-green-800 mb-4">
                    <p><strong>Cooling-Off Period:</strong> {coolingOff.daysRemaining} days remaining</p>
                    <p><strong>Refund Amount:</strong> ${subscription.totalPaid}</p>
                    <p><strong>Processing Time:</strong> 5-7 business days</p>
                  </div>
                  <button
                    onClick={handleCancel}
                    className="px-6 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700"
                  >
                    Cancel & Get Full Refund
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-yellow-50 border-2 border-yellow-300 rounded-xl p-6 mb-6">
              <div className="flex items-start gap-4">
                <AlertCircle size={48} className="text-yellow-600 flex-shrink-0" />
                <div>
                  <h3 className="text-xl font-bold text-yellow-900 mb-2">Cooling-Off Period Expired</h3>
                  <p className="text-sm text-yellow-800 mb-4">
                    Your 90-day cooling-off period ended on {coolingOff.endsDate}. You can still cancel to stop future charges, but previous payments are non-refundable.
                  </p>
                  <button
                    onClick={handleCancel}
                    className="px-6 py-3 bg-yellow-600 text-white font-semibold rounded-lg hover:bg-yellow-700"
                  >
                    Cancel Subscription (No Refund)
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Before You Go */}
          <div className="mb-6">
            <h3 className="font-bold text-gray-900 mb-4">Before You Go, Try This:</h3>
            <div className="grid gap-3">
              <button className="flex items-center gap-3 p-4 border-2 border-blue-200 rounded-lg hover:bg-blue-50 text-left">
                <Clock size={24} className="text-blue-600 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-gray-900">📞 Talk to Support</p>
                  <p className="text-sm text-gray-600">We might be able to help</p>
                </div>
              </button>
              <button className="flex items-center gap-3 p-4 border-2 border-purple-200 rounded-lg hover:bg-purple-50 text-left">
                <Pause size={24} className="text-purple-600 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-gray-900">⏸️ Pause Subscription</p>
                  <p className="text-sm text-gray-600">Keep your data, pause billing</p>
                </div>
              </button>
              <button className="flex items-center gap-3 p-4 border-2 border-green-200 rounded-lg hover:bg-green-50 text-left">
                <TrendingDown size={24} className="text-green-600 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-gray-900">💸 Switch to Basic</p>
                  <p className="text-sm text-gray-600">Lower tier ($9.99/month)</p>
                </div>
              </button>
            </div>
          </div>

          {/* Cancellation Reason */}
          <div className="mb-6">
            <h3 className="font-bold text-gray-900 mb-3">Tell Us Why You&apos;re Cancelling:</h3>
            <div className="space-y-2">
              {[
                'Too expensive',
                'Not using it enough',
                'Found alternative solution',
                'Technical issues',
                "Didn't meet my needs",
                'Other'
              ].map((reason) => (
                <label key={reason} className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                  <input
                    type="radio"
                    name="cancelReason"
                    value={reason}
                    checked={cancelReason === reason}
                    onChange={(e) => setCancelReason(e.target.value)}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm text-gray-700">{reason}</span>
                </label>
              ))}
            </div>
            {cancelReason === 'Other' && (
              <textarea
                value={otherReason}
                onChange={(e) => setOtherReason(e.target.value)}
                className="w-full mt-3 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                rows={3}
                placeholder="Please tell us more..."
              />
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={() => navigate('/settings')}
              className="flex-1 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold rounded-lg hover:shadow-lg"
            >
              Keep My Subscription
            </button>
            <button
              onClick={handleCancel}
              className="flex-1 py-3 border-2 border-red-300 text-red-700 font-semibold rounded-lg hover:bg-red-50"
            >
              Cancel Subscription
            </button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default CancelSubscription;
